/**
 * Discord Context - provides Discord SDK state throughout the app
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { initializeDiscordSdk, DiscordUserInfo, isMockMode } from '../discord/discordSdk';
import { getUserId } from '../utils';

interface DiscordContextValue {
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  oderId: string;        // Discord user ID (or mock ID)
  username: string;
  instanceId: string;
  roomId: string;          // guildId or instanceId for room routing
  isMockMode: boolean;
}

const DiscordContext = createContext<DiscordContextValue | null>(null);

interface DiscordProviderProps {
  children: ReactNode;
}

export function DiscordProvider({ children }: DiscordProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<DiscordUserInfo | null>(null);

  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        setIsLoading(true);
        setError(null);

        const info = await initializeDiscordSdk();

        if (mounted) {
          setUserInfo(info);
          setIsInitialized(true);
        }
      } catch (err) {
        console.error('Failed to initialize Discord SDK:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize Discord');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, []);

  // Determine roomId: use guildId if available, otherwise instanceId, or fallback
  const roomId = userInfo?.guildId || userInfo?.instanceId || 'default-room';

  // In mock mode, use local storage userId as oderId
  const oderId = isMockMode()
    ? getUserId()
    : (userInfo?.oderId || getUserId());

  const value: DiscordContextValue = {
    isInitialized,
    isLoading,
    error,
    oderId,
    username: userInfo?.username || 'Unknown',
    instanceId: userInfo?.instanceId || '',
    roomId,
    isMockMode: isMockMode(),
  };

  return (
    <DiscordContext.Provider value={value}>
      {children}
    </DiscordContext.Provider>
  );
}

/**
 * Hook to access Discord context
 */
export function useDiscord(): DiscordContextValue {
  const context = useContext(DiscordContext);
  if (!context) {
    throw new Error('useDiscord must be used within a DiscordProvider');
  }
  return context;
}
